import React from 'react';

import {DataFrame} from 'data-frames/DataFrameTypes';
import updateColumnDef from 'data-frames/util/updateColumnDef';
import {AnalyticsVisualizationAggregationType as AggregationType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import PieChart from 'ui-library/charting/PieChart';
import {aggregateChartDataFrame} from 'ui-library/charting/statistics/aggregation/aggregateChartDataFrame';

import {AGGREGATION_TYPE_TO_READABLE} from '../AnalyticsVisualizationConstants';
import convertPieChartProps from './prop-converters/convertPieChartProps';

type PieChartParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationPieChartParametersNode'
>;

interface PieChartViewerProps {
  parameters: PieChartParameters;
  dataFrame: DataFrame;
}

export default function PieChartViewer({
  parameters,
  dataFrame: rawDataFrame,
}: PieChartViewerProps): JSX.Element {
  const aggregationType = parameters.aggregationType ?? AggregationType.COUNT;
  const conversionOutput = convertPieChartProps(rawDataFrame, parameters);

  if (!conversionOutput.valid) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={conversionOutput.message} />;
  }

  const {dataFrame, columnsMapping} = conversionOutput.props;
  const {dataFrame: aggregatedDataFrame, columnsMapping: aggregatedColumnsMapping} = aggregateChartDataFrame(
    dataFrame,
    {
      partitionColumn: columnsMapping.sliceName,
      valueColumn: columnsMapping.measure,
    },
    aggregationType
  );

  const renamedDataFrame =
    aggregationType === AggregationType.COUNT
      ? updateColumnDef(aggregatedDataFrame, aggregatedColumnsMapping.aggregatedValues, {
          displayName: AGGREGATION_TYPE_TO_READABLE[aggregationType],
        })
      : aggregatedDataFrame;

  return (
    <PieChart
      {...conversionOutput.props}
      columnsMapping={{
        sliceName: aggregatedColumnsMapping.partitionColumn,
        measure: aggregatedColumnsMapping.aggregatedValues,
      }}
      dataFrame={renamedDataFrame}
    />
  );
}
